import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { PremiumModal } from '@/components/modals/PremiumModal';
import type { FeatureUpsellReason } from '@/lib/features';

export interface PaywallOptions {
  reason?: FeatureUpsellReason;
  customMessage?: string;
  /**
   * Abertura AUTOMÁTICA — disparada por um efeito de tela, não por um gesto do
   * usuário. Uma vez dispensada, ela não se repete nesta sessão (ver D11).
   * Omitir significa "o usuário pediu", e aí o paywall abre sempre.
   */
  auto?: boolean;
}

export interface PaywallState extends Required<Pick<PaywallOptions, 'reason'>> {
  open: boolean;
  customMessage?: string;
}

export type GlobalPaywall = readonly [
  PaywallState,
  (options?: PaywallOptions) => void,
  () => void,
];

const INITIAL_STATE: PaywallState = {
  open: false,
  reason: 'feature',
};

const PaywallContext = createContext<GlobalPaywall | undefined>(undefined);

export function PaywallProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PaywallState>(INITIAL_STATE);

  /**
   * D11 — o usuário GRÁTIS ficava PRESO no paywall de `/fiscal`.
   *
   * A `PaywallActivity` do RevenueCat é NATIVA e não tem botão de fechar: a
   * única saída é o VOLTAR do sistema. Só que voltar fechava a Activity e o app
   * apresentava OUTRA — medido em duas contas, com o hash do `ActivityRecord`
   * mudando a cada toque (Activity nova, não a mesma persistindo). Efeito
   * prático: abrir "Nota Fiscal" no grátis exigia MATAR o app.
   *
   * A causa é que uma abertura AUTOMÁTICA (efeito de tela) se redispara quando
   * o app volta do RevenueCat e `AuthContext`/`SubscriptionContext`
   * ressincronizam (#48/#110). A memória de dispensa mora AQUI, e não na
   * página, de propósito: o provider sobrevive à remontagem da rota, então o
   * guard vale mesmo que a árvore da tela seja recriada no resume — que é
   * justamente o que um guard por montagem não cobre.
   *
   * Só a abertura automática é silenciada. O botão "Assinar PRO" da tela é um
   * gesto do usuário e abre sempre — senão o guard viraria uma parede entre o
   * usuário e a assinatura.
   */
  const dispensadasAutomaticas = useRef(new Set<FeatureUpsellReason>());
  /** O que está aberto agora, para o `close` saber se foi automático. */
  const aberturaAtual = useRef<{ reason: FeatureUpsellReason; auto: boolean } | null>(null);

  const open = useCallback((options: PaywallOptions = {}) => {
    const reason = options.reason ?? 'feature';
    if (options.auto && dispensadasAutomaticas.current.has(reason)) return;

    aberturaAtual.current = { reason, auto: options.auto === true };
    setState({
      open: true,
      reason,
      customMessage: options.customMessage,
    });
  }, []);

  const close = useCallback(() => {
    const atual = aberturaAtual.current;
    if (atual?.auto) dispensadasAutomaticas.current.add(atual.reason);
    aberturaAtual.current = null;
    setState((current) => ({ ...current, open: false }));
  }, []);

  const value = useMemo<GlobalPaywall>(() => [state, open, close], [state, open, close]);

  return (
    <PaywallContext.Provider value={value}>
      {children}
      <PremiumModal
        open={state.open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) close();
        }}
        reason={state.reason}
        customMessage={state.customMessage}
      />
    </PaywallContext.Provider>
  );
}

export function useGlobalPaywall(): GlobalPaywall {
  const context = useContext(PaywallContext);
  if (!context) {
    throw new Error('useGlobalPaywall must be used within a PaywallProvider');
  }
  return context;
}
